import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_API_URL } from '../helpers/Config';

const CommissionScreen = ({ theme = 'dark' }) => {
    const [commissionData, setCommissionData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isDark = theme === 'dark';
    const styles = getStyles(isDark);

    // API call to fetch commission data
    const fetchCommissionData = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(`${BASE_API_URL}/team/commission-chart`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
            });

            const data = response?.data;

            const transformedData = transformCommissionData(data?.data);
            setCommissionData(transformedData);
        } catch (err) {
            console.error('Error fetching commission data:', err);
            setError('Failed to load commission data');
        } finally {
            setLoading(false);
        }
    };

    // Transform API response to match the table structure
    const transformCommissionData = (apiData) => {
        if (!Array.isArray(apiData)) {
            console.error('Expected array but got:', apiData);
            throw new Error('Invalid data format');
        }

        const levels = 10;
        const transformed = [];

        for (let level = 1; level <= levels; level++) {
            const stakingCommission = apiData.find(
                (item) => item.type === 'staking' && item.level === level
            );
            const tradeCommission = apiData.find(
                (item) => item.type === 'trade' && item.level === level
            );
            const referCommission = apiData.find(
                (item) => item.type === 'refer' && item.level === level
            );
            const depositCommission = apiData.find(
                (item) => item.type === 'deposit' && item.level === level
            );

            transformed.push({
                level: level,
                trade: tradeCommission
                    ? `${parseFloat(tradeCommission.commission).toFixed(4)}%`
                    : 'N/A',
                refer: referCommission
                    ? `${parseFloat(referCommission.commission).toFixed(1)}%`
                    : 'N/A',
                deposit: depositCommission
                    ? `${parseFloat(depositCommission.commission).toFixed(1)}%`
                    : 'N/A',
                staking: stakingCommission
                    ? `${parseFloat(stakingCommission.commission).toFixed(1)}%`
                    : 'N/A',
            });
        }

        return transformed;
    };

    useEffect(() => {
        fetchCommissionData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rowVariants = {
        hidden: { opacity: 0, x: -50 },
        visible: (i) => ({
            opacity: 1,
            x: 0,
            transition: {
                delay: i * 0.1,
                duration: 0.5,
                ease: 'easeOut',
            },
        }),
    };

    const getCommissionColor = (value, type) => {
        if (value === 'N/A') return 'text-muted';

        const numValue = parseFloat(value);
        if (type === 'trade') {
            return numValue >= 0.5
                ? 'text-success'
                : numValue >= 0.1
                    ? 'text-info'
                    : 'text-warning';
        } else {
            return numValue >= 5
                ? 'text-success'
                : numValue >= 2
                    ? 'text-info'
                    : 'text-warning';
        }
    };

    // Loading state
    if (loading) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="h-100 d-flex align-items-center justify-content-center"
                style={styles.container}
            >
                <div className="text-center">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="spinner-border text-primary mb-3"
                        role="status"
                    >
                        <span className="visually-hidden">Loading...</span>
                    </motion.div>
                    <p style={styles.mutedText}>Loading commission data...</p>
                </div>
            </motion.div>
        );
    }

    // Error state
    if (error) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="h-100 d-flex align-items-center justify-content-center"
                style={styles.container}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center"
                >
                    <div className="mb-3" style={styles.errorIcon}>
                        ⚠️
                    </div>
                    <h5 style={styles.errorTitle}>Failed to Load</h5>
                    <p style={styles.mutedText}>{error}</p>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={fetchCommissionData}
                        className="btn btn-primary mt-2"
                    >
                        Try Again
                    </motion.button>
                </motion.div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="h-100"
            style={styles.container}
        >
            {/* Header with Icon */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="d-flex align-items-center mb-4"
            >
                <motion.div
                    className="rounded-circle d-flex align-items-center justify-content-center me-3"
                    style={styles.headerIconWrapper}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                >
                    <span className="fw-bold" style={styles.headerIconText}>
                        💰
                    </span>
                </motion.div>
                <div>
                    <h3 className="fw-bold mb-0" style={styles.headerTitle}>
                        Commission
                    </h3>
                    <small style={styles.headerSubtitle}>
                        Multi-level Commission Structure
                    </small>
                </div>
            </motion.div>

            {/* Commission Table */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="card border-0 shadow-sm"
                style={styles.card}
            >
                <div className="card-body p-0">
                    <div
                        className="table-responsive"
                        style={{
                            overflowX: 'auto',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                        }}
                    >
                        <style>
                            {`
                                .table-responsive::-webkit-scrollbar {
                                    display: none;
                                }
                            `}
                        </style>
                        <table className="table table-hover mb-0 table-sm">
                            <thead>
                                <motion.tr
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4, duration: 0.5 }}
                                    style={styles.tableHeaderRow}
                                >
                                    <th
                                        scope="col"
                                        className="ps-4 py-3 fw-bold"
                                        style={styles.tableHeaderText}
                                    >
                                        Level
                                    </th>
                                    <th
                                        scope="col"
                                        className="py-3 fw-bold"
                                        style={styles.tableHeaderText}
                                    >
                                        Trade
                                    </th>
                                    <th
                                        scope="col"
                                        className="py-3 fw-bold"
                                        style={styles.tableHeaderText}
                                    >
                                        Refer
                                    </th>
                                    <th
                                        scope="col"
                                        className="py-3 fw-bold"
                                        style={styles.tableHeaderText}
                                    >
                                        Deposit
                                    </th>
                                    <th
                                        scope="col"
                                        className="pe-4 py-3 fw-bold"
                                        style={styles.tableHeaderText}
                                    >
                                        R-Trade
                                    </th>
                                </motion.tr>
                            </thead>
                            <tbody>
                                {commissionData.map((item, index) => (
                                    <motion.tr
                                        key={item.level}
                                        custom={index}
                                        initial="hidden"
                                        animate="visible"
                                        variants={rowVariants}
                                        whileHover={{
                                            backgroundColor: isDark
                                                ? 'rgba(59,130,246,0.15)'
                                                : 'rgba(0,123,255,0.05)',
                                            scale: 1.01,
                                            transition: { duration: 0.2 },
                                        }}
                                        className="border-bottom"
                                        style={styles.tableRow}
                                    >
                                        <td className="ps-4 py-3 fw-bold">
                                            <motion.span
                                                className="badge bg-primary rounded-pill"
                                                whileHover={{ scale: 1.1 }}
                                                transition={{
                                                    type: 'spring',
                                                    stiffness: 400,
                                                }}
                                                style={styles.levelBadge}
                                            >
                                                L{item.level}
                                            </motion.span>
                                        </td>
                                        <td className="py-3">
                                            <motion.span
                                                className={`fw-bold ${getCommissionColor(
                                                    item.trade,
                                                    'trade'
                                                )}`}
                                                whileHover={{ scale: 1.05 }}
                                                style={styles.cellText}
                                            >
                                                {item.trade}
                                            </motion.span>
                                        </td>
                                        <td className="py-3">
                                            <motion.span
                                                className={`fw-bold ${getCommissionColor(
                                                    item.refer,
                                                    'refer'
                                                )}`}
                                                whileHover={{ scale: 1.05 }}
                                                style={styles.cellText}
                                            >
                                                {item.refer}
                                            </motion.span>
                                        </td>
                                        <td className="py-3">
                                            <motion.span
                                                className={`fw-bold ${getCommissionColor(
                                                    item.deposit,
                                                    'deposit'
                                                )}`}
                                                whileHover={{ scale: 1.05 }}
                                                style={styles.cellText}
                                            >
                                                {item.deposit}
                                            </motion.span>
                                        </td>
                                        <td className="pe-4 py-3">
                                            <motion.span
                                                className={`fw-bold ${getCommissionColor(
                                                    item.staking,
                                                    'staking'
                                                )}`}
                                                whileHover={{ scale: 1.05 }}
                                                style={styles.cellText}
                                            >
                                                {item.staking}
                                            </motion.span>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>

            {/* Note Section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="mt-4"
            >
                <motion.div
                    className="card border-0"
                    style={styles.noteCard}
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                >
                    <div className="card-body py-3">
                        <div className="row align-items-center">
                            <div className="col-auto">
                                <span style={styles.noteIcon}>💡</span>
                            </div>
                            <div className="col">
                                <h6 className="fw-bold mb-1" style={styles.noteTitle}>
                                    Commission Notes
                                </h6>
                                <small style={styles.noteText}>
                                    • Trade commission available for levels 1-6 only<br />
                                    • Refer and Deposit commissions available for levels 1-6<br />
                                    • Higher levels focus on staking rewards with tiered
                                    percentages<br />
                                    • All percentages are based on your referral network activity
                                </small>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

const getStyles = (isDark) => ({
    container: {
        minHeight: '100%',
        backgroundColor: isDark ? '#020617' : '#f8f9fa',
        color: isDark ? '#e5e7eb' : '#111827',
        padding: '0.5rem',
        borderRadius: '0.75rem',
    },
    mutedText: {
        color: isDark ? '#9ca3af' : '#6c757d',
        fontSize: '0.9rem',
    },
    errorIcon: {
        fontSize: '2rem',
        color: isDark ? '#f97373' : '#dc3545',
    },
    errorTitle: {
        color: isDark ? '#fecaca' : '#dc3545',
    },
    headerIconWrapper: {
        width: '45px',
        height: '45px',
        backgroundColor: isDark ? '#1d4ed8' : '#0d6efd',
    },
    headerIconText: {
        fontSize: '1.1rem',
        color: '#ffffff',
    },
    headerTitle: {
        fontSize: '1.4rem',
        color: isDark ? '#e5e7eb' : '#111827',
    },
    headerSubtitle: {
        fontSize: '0.8rem',
        color: isDark ? '#9ca3af' : '#6c757d',
    },
    card: {
        backgroundColor: isDark ? '#020617' : '#ffffff',
        borderRadius: '12px',
        border: isDark ? '1px solid #1f2937' : 'none',
        boxShadow: isDark
            ? '0 10px 25px rgba(0,0,0,0.7)'
            : '0 .125rem .25rem rgba(0,0,0,.075)',
    },
    tableHeaderRow: {
        backgroundColor: isDark ? '#020617' : '#f8f9fa',
        borderBottom: `1px solid ${isDark ? '#1f2937' : '#dee2e6'}`,
    },
    tableHeaderText: {
        fontSize: '0.9rem',
        color: isDark ? '#60a5fa' : '#0d6efd',
        whiteSpace: 'nowrap',
    },
    tableRow: {
        backgroundColor: isDark ? 'transparent' : '#ffffff',
    },
    levelBadge: {
        fontSize: '0.8rem',
    },
    cellText: {
        fontSize: '0.85rem',
    },
    noteCard: {
        backgroundColor: isDark ? '#020617' : '#f8f9fa',
        borderRadius: '12px',
        border: isDark ? '1px solid #1f2937' : 'none',
    },
    noteIcon: {
        fontSize: '1.1rem',
        color: isDark ? '#60a5fa' : '#0d6efd',
    },
    noteTitle: {
        fontSize: '0.9rem',
        color: isDark ? '#60a5fa' : '#0d6efd',
    },
    noteText: {
        fontSize: '0.8rem',
        color: isDark ? '#9ca3af' : '#6c757d',
    },
});

export default CommissionScreen;
